import React, { useState } from "react";
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Label,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import { getColourSchemeByID } from "../../../Colors/ColorPalettes";
import { formatNumber } from "../Utils/FormatNumbers";
import { CustomTooltip } from "../Custom/CustomTooltipContent";
import { castToNumberData } from "../Utils/transformData";
import { dispatchSortValues } from "../Utils/ChartsFunctions";
import { setSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import { sortData } from "../Utils/SortData";

const sortValueKeys = (valueKeys) => {
  if (valueKeys.length < 2) {
    return valueKeys;
  }

  const customOrder = ["area", "bar", "line"];
  console.log(valueKeys);

  valueKeys.sort((a, b) => {
    console.log(a.display, b.display);

    const indexA = customOrder.indexOf(a.display);
    const indexB = customOrder.indexOf(b.display);

    if (indexA !== -1 && indexB !== -1) {
      // If both elements are in the custom order, sort by their order
      return indexA - indexB;
    } else if (indexA !== -1) {
      // If only A is in the custom order, it comes first
      return -1;
    } else if (indexB !== -1) {
      // If only B is in the custom order, it comes first
      return 1;
    } else {
      // If neither is in the custom order, sort alphabetically
      return a.display.localeCompare(b.display);
    }
  });

  return valueKeys; // Ensure the sorted array is returned
};

const Combochart = ({
  Data: incomingData,
  Sheetid,
  rowKeys: rawRowkeys,
  toolTipKeys = [],
  groupByKeys,
  valueKeys = [],
  sortProperties,
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );
  const dispatch = useDispatch();
  const rowKeys = rawRowkeys ? rawRowkeys.map((item) => item.responseKey) : [];
  const responseKeys = valueKeys.map((item) => item.responseKey);
  const Data = castToNumberData(incomingData, responseKeys);
  const SufixPrefix = { ...valueKeys.map((item) => item.numberFormat)[0] };

  // Ensure groupByKeys and valueKeys exist and have responseKey property
  if (
    !groupByKeys ||
    !groupByKeys[0] ||
    !groupByKeys[0].responseKey ||
    valueKeys.length === 0
  ) {
    return null;
  }

  const {
    colorSchemeid,
    ComboLinepointSize,
    isComboBarLabelVisible,
    isComboLineLabelVisible,
    labelComboBarPosition,
    labelComboBarFontSize,
    labelComboBarFontColor,
    labelComboBarRotate,
    labelComboLinePosition,
    labelComboLineFontSize,
    labelComboLineFontColor,
    labelComboLineRotate,
    isComboAreaLabelVisible,
    labelComboAreaPosition,
    labelComboAreaFontSize,
    labelComboAreaFontColor,
    labelComboAreaRotate,
    colorMapping,

  } = customizationObject;

  console.log(isComboLineLabelVisible);
  const newArray =
    valueKeys.length > 0 ? valueKeys.map((vItem) => vItem.responseKey) : [];
  console.log(newArray);
  if (groupByKeys.length > 0) {
    dispatchSortValues({ newArray, dispatch, setSheetControls, Sheetid });
  }
  const sorteData = sortData({
    transformedData: Data,
    sortProperties,
    requiredArray: groupByKeys,
  });
  const colorScheme = getColourSchemeByID(colorSchemeid);
  const COLORS = colorScheme?.colors;
  console.log(sorteData);
  const barSize = customizationObject.barSize;
  const isGridVisible = customizationObject.isGridVisible;
  const isTransposeVisible = customizationObject.isTransposeVisible;
  const isLabelVisible = customizationObject.isLabelVisible;
  const labelPosition = customizationObject.labelPosition;
  const labelFontSize = customizationObject.labelFontSize;
  const labelFontColor = customizationObject.labelFontColor;
  const labelRotate = customizationObject.labelRotate;

  const sortedValueKeys = sortValueKeys(valueKeys);
  console.log(sortedValueKeys);

  const tickFormatter = (tick) => {
    if (typeof tick === "number") {
      return formatNumber(tick, SufixPrefix);
    }
    return tick;
  };
  const handleComboMouseEnter = (lineIndex) => {
    setHoverIndex(lineIndex);
  };

  const handleComboMouseLeave = () => {
    setHoverIndex(null);
  };
  return (
    <ResponsiveContainer width="100%" height={"100%"}>
      <ComposedChart
        data={sorteData}
        layout={isTransposeVisible ? "vertical" : "horizontal"}
      >
        <defs>
          <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={COLORS} stopOpacity={0.8} />
            <stop offset="95%" stopColor={COLORS} stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* Optional CartesianGrid */}
        {isGridVisible && <CartesianGrid strokeDasharray="3 3" />}

        {/* X and Y Axis */}
        <XAxis
          dataKey={groupByKeys[0].responseKey}
          tickFormatter={isTransposeVisible ? tickFormatter : null}
        >
          <Label offset={-5} position="insideBottom" />
        </XAxis>
        {/* independentAxis */}
        <YAxis
          yAxisId="left"
          type={isTransposeVisible ? "category" : "number"}
          dataKey={isTransposeVisible ? rowKeys[0] : null}
          tickFormatter={tickFormatter}
        >
          <Label angle={-90} position="insideLeft" />
        </YAxis>

        <YAxis
          yAxisId="right"
          orientation="right"
          type={isTransposeVisible ? "category" : "number"}
          dataKey={isTransposeVisible ? rowKeys[0] : null}
          tickFormatter={tickFormatter}
        ></YAxis>
        <Tooltip
          content={
            <CustomTooltip
              chartType={"combochart"}
              tooltipKeys={toolTipKeys}
              SufixPrefix={SufixPrefix}
              colorSchemeid={colorSchemeid}
              columnKeys={groupByKeys}
              valueKeys={valueKeys}
              currentLineIndex={hoverIndex}
              colorMapping={colorMapping}
            />
          }
        />
        <Legend verticalAlign="top" height={36} />

        {/* Render different chart types based on valueKeys */}
        {sortedValueKeys.map((key, index) => {
          const baseColor =
            colorMapping?.[key?.responseKey] || COLORS[index % COLORS.length];


          const numberFormatObject = key.numberFormat;
          const independentKey = key.independentAxis;
          const axisId = independentKey === true ? "right" : "left";

          if (key.display === "bar") {

            // Render regular Bar chart
            return (
              <Bar
                key={`regular-bar-${index}`}
                dataKey={key.responseKey}
                yAxisId={axisId}
                fill={baseColor}
                // fill={COLORS[index % COLORS.length]}
                barSize={barSize}
                onMouseEnter={() => handleComboMouseEnter(index)}
                onMouseLeave={handleComboMouseLeave}
              >
                {/* label only bar chart  only */}
                {isComboBarLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelComboBarPosition?.value}
                    fontSize={labelComboBarFontSize}
                    fill={labelComboBarFontColor}
                    angle={labelComboBarRotate}
                    formatter={(value) =>
                      formatNumber(value, numberFormatObject)
                    }
                  />
                )}

                {/* combochart  */}
                {/* {isLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelPosition}
                    fontSize={labelFontSize}
                    fill={labelFontColor}
                    formatter={(value) => formatNumber(value, numberFormatObject)}
                  />
                )} */}
              </Bar>
            );
          }
          if (key.display === "area") {
            // Render Area chart
            return (
              <Area
                key={`area-chart-${index}`}
                type="monotone"
                dataKey={key.responseKey}

                yAxisId={axisId}
                // stroke={COLORS[index % COLORS.length]}
                stroke={baseColor}
                fillOpacity={0.5}
                // fill={COLORS[index % COLORS.length]}
                fill={baseColor}
                onMouseEnter={() => handleComboMouseEnter(index)}
                onMouseLeave={handleComboMouseLeave}
              >
                {isComboAreaLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelComboAreaPosition?.value}
                    fontSize={labelComboAreaFontSize}
                    fill={labelComboAreaFontColor}
                    angle={labelComboAreaRotate}
                    formatter={(value) =>
                      formatNumber(value, numberFormatObject)
                    }
                  />
                )}
                {/* //are combochart */}
                {/* {isLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelPosition}
                    fontSize={labelFontSize}
                    fill={labelFontColor}
                    formatter={(value) => formatNumber(value, numberFormatObject)}
                  />
                )} */}
              </Area>
            );
          }

          if (key.display === "line") {
            // Render Line chart
            return (
              <Line
                key={`line-chart-${index}`}
                type="monotone"
                dataKey={key.responseKey}
                // stroke={getColor("line")}
                yAxisId={axisId}
                // stroke={COLORS[index % COLORS.length]}
                stroke={baseColor}
                strokeWidth={2}
                onMouseEnter={() => handleComboMouseEnter(index)}
                onMouseLeave={handleComboMouseLeave}

                dot={
                  hoverIndex === index
                    ? {
                      r: ComboLinepointSize + 1, // Bigger dot on hover
                      onMouseEnter: () => handleComboMouseEnter(index),
                      onMouseLeave: handleComboMouseLeave,
                    }
                    : false
                }


              >
                {isComboLineLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelComboLinePosition?.value}
                    fontSize={labelComboLineFontSize}
                    fill={labelComboLineFontColor}
                    angle={labelComboLineRotate}
                    formatter={(value) =>
                      formatNumber(value, numberFormatObject)
                    }
                  />
                )}
                {/* combo line label */}
                {/* {isLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelPosition}
                    fontSize={labelFontSize}
                    fill={labelFontColor}
                    formatter={(value) => formatNumber(value, numberFormatObject)}
                  />
                )} */}
              </Line>
            );
          }

          if (key.display === "stackbar") {
            // Render stacked bars
            return (
              <>
                <Bar
                  key={`stacked-bar1-${index}`}
                  dataKey={key.responseKey}
                  stackId="a"

                  // fill={COLORS[index % COLORS.length]}
                  fill={baseColor}
                  barSize={barSize}
                  onMouseEnter={() => handleComboMouseEnter(index)}
                  onMouseLeave={handleComboMouseLeave}
                >
                  {isLabelVisible && (
                    <LabelList
                      dataKey={key.responseKey}
                      position={labelPosition}
                      fontSize={labelFontSize}
                      fill={labelFontColor}
                      angle={labelRotate}
                    />
                  )}
                </Bar>
                <Bar
                  key={`stacked-bar2-${index}`}
                  dataKey={key.responseKey}
                  stackId="a"

                  // fill={COLORS[index % COLORS.length]}
                  fill={baseColor}
                  barSize={barSize}
                  onMouseEnter={() => handleComboMouseEnter(index)}
                  onMouseLeave={handleComboMouseLeave}
                >
                  {isLabelVisible && (
                    <LabelList
                      dataKey={key.responseKey}
                      position={labelPosition}
                      fontSize={labelFontSize}
                      fill={labelFontColor}
                      angle={labelRotate}
                    />
                  )}
                </Bar>
              </>
            );
          }

          return null; // Return null for unsupported chart types
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default Combochart;
